import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { PhoneIcon } from '@heroicons/react/24/solid';
import mixpanel from 'mixpanel-browser';
import ReactGA from "react-ga4";

mixpanel.init('f4710b9de9fbf50c638ae49451c3261f', { debug: false });
ReactGA.initialize("G-M6184ES3SR");

// Logo Component
function BcallLogo({ className = "w-12 h-12" }: { className?: string }) {
  return (
    <svg 
      className={className} 
      viewBox="0 0 222.62 195.41" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path 
        fill="none"
        stroke="#8bc34a"
        strokeLinecap="round"
        strokeMiterlimit={10}
        strokeWidth="16.52px" 
        d="M148.29,106.01c0-20.03-17.39-36.52-38.51-36.52-18.49,0-34.11,12.62-37.73,29.2-.95,4.34-4.98,7.32-9.42,7.32h-11.68c-5.82,0-10.4-5.21-9.39-10.95,5.58-31.8,35.02-56.36,70.03-56.36s66.23,26.08,70.47,59.27c.59,4.64,4.69,8.03,9.37,8.03h13.19c5.69,0,10.1-4.98,9.44-10.63-5.66-48.7-49.7-87.12-102.47-87.12C55.04,8.26,8.5,52.38,8.5,106.01"
      />
      <path 
        fill="#8bc34a" 
        d="M120.76,165.99c-6.28,.74-12.62,.74-18.91,0-14.49-1.71-40.64-6.59-41.71-19.67-1.49-18.13-16.34-17.05-16.34-17.05,0,0-25.96,.03-34.27,0-5.67-.02-8.47,4.97-8.47,4.97-9.8,15.82,49.93,61.15,110.25,61.15s120.05-45.34,110.25-61.15c0,0-2.81-4.99-8.47-4.97-8.31,.03-34.27,0-34.27,0,0,0-14.85-1.08-16.34,17.05-1.08,13.08-27.22,17.96-41.71,19.67Z"
      />
    </svg>
  );
}

// Loading Spinner
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-bcall-200 border-t-bcall-500 rounded-full animate-spin"></div>
        <p className="text-gray-500 font-medium">Loading...</p>
      </div>
    </div>
  );
}

// Error Card
function ErrorCard() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-call-card p-8 max-w-sm w-full animate-fade-in">
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Customer Not Found</h1>
          <p className="text-gray-500 mb-6">The dialer link you're trying to access doesn't exist or has expired.</p>
          <BcallLogo className="w-16 h-16 opacity-50" />
        </div>
      </div>
    </div>
  );
}

function App() {
  const navigate = useNavigate();
  const queryString = window.location.pathname;
  
  const [loading, setLoading] = useState(true);
  const [number, setNumber] = useState("");
  const [ext, setExt] = useState("");
  const [country, setCountry] = useState("");
  const [desktop, setDesktop] = useState(true);
  const [status, setStatus] = useState(false);
  const [name, setName] = useState(queryString.slice(1));
  const [realnumber, setRealumber] = useState("");
  const [startingCall, setStartingCall] = useState(false);

  const startKamailioCall = async () => {
    setStartingCall(true);
    try {
      const response = await fetch("https://dialer1.bcall.us/call-sessions", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: queryString.slice(1),
          callerId: document.referrer ? new URL(document.referrer).hostname : "Bcall visitor"
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Unable to start call");

      sessionStorage.setItem("userpass", result.userpass);
      sessionStorage.setItem("password", result.password);
      sessionStorage.setItem("numberadv", result.targetSipUser);
      sessionStorage.setItem("sipDomain", result.domain);
      sessionStorage.setItem("sipServer", result.websocket);
      sessionStorage.setItem("sessionId", result.sessionId);
      sessionStorage.setItem("sessionToken", result.sessionToken);
      sessionStorage.setItem("turnuser", result.turn.username);
      sessionStorage.setItem("turnpassword", result.turn.password);
      navigate('/dialer');
    } catch (error) {
      console.error("Failed to create Kamailio call session", error);
      setStatus(true);
    } finally {
      setStartingCall(false);
    }
  };

  useEffect(() => {
    fetch("https://dialer1.bcall.us/dialer-info?name=" + queryString.slice(1))
      .then(res => res.json())
      .then(
        (result) => {
          setLoading(false);
          
          if (result.status === "404") {
            setStatus(true);
          } else {
            setStatus(false);
            mixpanel.identify(queryString.slice(1));
            mixpanel.track('Show dialer', {
              'Country': result.country,
              'Device': result.device
            });
            mixpanel.people.increment("Show_dialer_count", 1);

            setNumber(result.number || "");
            setExt(String(result.ext || ""));
            setCountry(result.country);
            setRealumber(result.realnumber);
            
            // Keep only profile/display metadata here.
            // Kamailio SIP/session credentials are created only when starting a call.
            sessionStorage.removeItem("sessionId");
            sessionStorage.removeItem("sessionToken");
            sessionStorage.removeItem("userpass");
            sessionStorage.removeItem("password");
            sessionStorage.removeItem("sipDomain");
            sessionStorage.removeItem("sipServer");
            sessionStorage.removeItem("turnuser");
            sessionStorage.removeItem("turnpassword");
            sessionStorage.setItem("ext", String(result.ext || ""));
            sessionStorage.setItem("number", String(result.number || ""));
            sessionStorage.setItem("numberadv", String(result.numberadv || ""));
            sessionStorage.setItem("realnumber", String(result.realnumber || ""));
            sessionStorage.setItem("name", queryString.slice(1));

            if (result.device !== "mobile") {
              setDesktop(true);
            }
            if (result.action === "Phone") {
              setDesktop(true);
            }
          }
        },
        (error) => {
          setLoading(false);
          setStatus(true);
        }
      );
  }, [queryString]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (status) {
    return <ErrorCard />;
  }

  // Main Call Card
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-call-card p-8 max-w-sm w-full animate-slide-up">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-bcall-50 rounded-full flex items-center justify-center">
            <BcallLogo className="w-14 h-14" />
          </div>
        </div>

        {/* Customer Name */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{name}</h1>
          <p className="text-gray-500">Click to start your call</p>
        </div>

        {/* Call Button */}
        {desktop ? (
          <button
            onClick={() => {
              mixpanel.track('Click call dialer');
              mixpanel.people.increment("Show_click_call_count", 1);
              startKamailioCall();
            }}
            disabled={startingCall}
            className="w-full bg-bcall-500 hover:bg-bcall-600 text-white font-semibold py-4 px-6 rounded-2xl 
                       flex items-center justify-center gap-3 transition-all duration-200 
                       shadow-button hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
          >
            <PhoneIcon className="w-6 h-6" />
            <span className="text-lg">{startingCall ? 'Connecting...' : 'Call Now'}</span>
          </button>
        ) : (
          <a
            href={`tel:${realnumber},${ext}#`}
            className="w-full bg-bcall-500 hover:bg-bcall-600 text-white font-semibold py-4 px-6 rounded-2xl 
                       flex items-center justify-center gap-3 transition-all duration-200 
                       shadow-button hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] no-underline"
          >
            <PhoneIcon className="w-6 h-6" />
            <span className="text-lg">Call Now</span>
          </a>
        )}

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-400">
            Powered by{' '}
            <a href="https://bcall.us/" className="text-bcall-500 hover:text-bcall-600 font-medium">
              Bcall.us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
